import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lightbulb, Flame, Smile, Mail, LineChart, Utensils } from 'lucide-react';

const ease: [number, number, number, number] = [0.22, 1, 0.36, 1];

const content = {
  students: {
    points: [
      { icon: Lightbulb, title: 'Hints when you need them', text: 'Dev nudges you towards the answer so you actually understand it.' },
      { icon: Flame, title: 'Streaks that forgive', text: 'Build a study habit, with freeze days for when life gets busy.' },
      { icon: Smile, title: 'No comparing with others', text: 'You only measure yourself against you — last week vs this week.' },
    ],
    preview: { label: 'Today’s plan', lines: ['Biology 3 · Cell membranes · 12 min', 'Maths Methods 4 · Derivatives · 15 min', 'Quick check · 5 questions'] },
  },
  parents: {
    points: [
      { icon: Mail, title: 'A report every Sunday', text: 'An honest summary lands in your inbox at noon — no logging in needed.' },
      { icon: LineChart, title: 'See real progress', text: 'Which subjects they studied, what’s improving, and where to focus next.' },
      { icon: Utensils, title: 'Dinner Table Conversations', text: 'Easy questions to ask about what they learnt this week.' },
    ],
    preview: { label: 'Tonight’s conversation starter', lines: ['“What surprised you about how cells let things in and out?”', '“Could you explain a derivative to me like I’m 12?”'] },
  },
};

export default function PersonaSwitcher() {
  const [tab, setTab] = useState<'students' | 'parents'>('students');
  const data = content[tab];

  return (
    <section className="py-16 md:py-24" aria-labelledby="persona-heading">
      <div className="container mx-auto px-6 md:px-8 lg:px-12 max-w-6xl">
        <h2 id="persona-heading" className="font-display text-3xl md:text-4xl font-bold text-center mb-8">
          Made for students. <span className="font-script italic text-accent">Trusted</span> by parents.
        </h2>

        <div role="tablist" className="relative mx-auto mb-12 flex w-fit rounded-full border border-border/60 bg-muted/40 p-1">
          {(['students', 'parents'] as const).map((t) => (
            <button
              key={t}
              role="tab"
              aria-selected={tab === t}
              onClick={() => setTab(t)}
              className={`relative z-10 min-h-[44px] px-6 text-sm font-semibold rounded-full transition-colors ${tab === t ? 'text-accent-foreground' : 'text-muted-foreground hover:text-foreground'}`}
            >
              {tab === t && (
                <motion.span layoutId="persona-pill" className="absolute inset-0 -z-10 rounded-full bg-accent" transition={{ duration: 0.4, ease }} />
              )}
              For {t === 'students' ? 'Students' : 'Parents'}
            </button>
          ))}
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={tab}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.45, ease }}
            className="grid lg:grid-cols-2 gap-10 items-center"
          >
            <div className="space-y-6">
              {data.points.map(({ icon: Icon, title, text }) => (
                <div key={title} className="flex gap-4">
                  <div className="w-11 h-11 shrink-0 rounded-full bg-accent/10 flex items-center justify-center">
                    <Icon className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1">{title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{text}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="glass-card rounded-2xl border border-border/50 bg-card/70 backdrop-blur-xl p-6 shadow-xl">
              <p className="text-xs uppercase tracking-[0.15em] text-accent font-semibold mb-4">{data.preview.label}</p>
              <ul className="space-y-3">
                {data.preview.lines.map((l) => (
                  <li key={l} className="rounded-xl bg-muted/50 px-4 py-3 text-sm text-foreground/90">{l}</li>
                ))}
              </ul>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
